import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable, Challenge, EntityTypeName, Entity } from '@shapeable/types';
import { breakpoints, theme, ColorTokens } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { ContentTab, EntityGrid, SliceLayoutBannerTabs, SliceEntityIconPageHeader, TabsContainer, UiComponentName, useComponentProps, useEntityTypes, usePageEntity, usePodcasts, usePosts, useVideos, SlicesRenderer } from '@shapeable/ui';
import { useReflections } from '../../hooks/use-reflections';
import { sortBy } from 'lodash';
const cls = classNames('challenge-layout');

// -------- Types -------->

// -------- Props -------->

export type ChallengeLayoutProps = Classable & HasChildren & { 
  entity?: Challenge;
  maxBodyWidth?: string;
};

export const ChallengeLayoutDefaultProps: Omit<ChallengeLayoutProps, 'entity'> = {
  maxBodyWidth: '1088px',
};

// -------- Child Component Props -------->

type ContainerProps = {
  children?: React.ReactNode;
  className?: string;
};

type BoundaryProps = {
  maxWidth?: string;
}

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
  
  `,
});

type HeaderProps = {
  $isWordmarkTitle?: boolean;
};

const HeaderStyles = breakpoints({
  base: css`
    ${({ $isWordmarkTitle }: HeaderProps) => $isWordmarkTitle && css`
      .shp--slice-entity-icon-page-header__icon {
        flex-shrink: 0;
        svg {
          width: 160px;
        }
      }

      .shp--slice-entity-icon-page-header__icon-and-name {
        justify-content: flex-end;
      }

      /* Stack until ~870px */
      div:has(> .shp--content-node) {
        display: flex;
        flex-direction: column;
      }

      @media (min-width: 870px) {
        .shp--slice-entity-icon-page-header__icon-and-name {
          svg {
            width: 200px;
          }
        }

        /* empty icon | title (wordmark) | description */
        div:has(> .shp--content-node) {
          display: grid;
          grid-template-columns: 180px auto 1fr;
          column-gap: 24px;
          align-items: center;
        }

        div:has(> .shp--content-node) > *:first-child {
          grid-column: 2;
        }

        div:has(> .shp--content-node) > .shp--content-node {
          grid-column: 3;
          min-width: 0;
        }
      }
    `}
  `,
  tablet: css`
    ${({ $isWordmarkTitle }: HeaderProps) => $isWordmarkTitle && css`
      .shp--slice-entity-icon-page-header__icon {
        svg {
          width: 200px;
        }
      }
    `}
  `,
  desktop: css`
    ${({ $isWordmarkTitle }: HeaderProps) => $isWordmarkTitle && css`
      .shp--slice-entity-icon-page-header__icon {
        svg {
          width: 280px;
        }
      }

      div:has(> .shp--content-node) {
        display: grid;
        grid-template-columns: 240px auto 1fr;
        column-gap: 40px;
        align-items: center;
      }

      div:has(> .shp--content-node) > *:first-child {
        grid-column: 2;
      }

      div:has(> .shp--content-node) > .shp--content-node {
        grid-column: 3;
        min-width: 0;
      }

      .shp--content-node {
        padding-bottom: ${theme.UNIT(3)};
      }
    `}
  `,
});

const BoundaryStyles = breakpoints({
  base: css`
    box-sizing: border-box;
    width: 100%;
    padding: 0 ${theme.UNIT(2)};
    ${({ maxWidth }: BoundaryProps ) => css`
      max-width: ${maxWidth || '100%'};
      margin: 0 auto;
    `}
  `,
});


const TabsStyles = breakpoints({
  base: css`
    margin-top: ${theme.UNIT(2)};
    margin-bottom: ${theme.UNIT(6)};
  `,
});

const TabStyles = breakpoints({
  base: css`
    .shp--content-tab__icon-label {
      white-space: nowrap;
    }
  `,
});


const GridStyles = breakpoints({
  base: css`
    margin-top: ${theme.UNIT(8)};
  `,
});





// -------- Components -------->

const My = {
  NAME: 'ChallengeLayout' as UiComponentName,

  Container: styled.div<ContainerProps>`${ContainerStyles}`,
    Tabs: styled(TabsContainer)<ContainerProps>`${TabsStyles}`,
      Tab: styled(ContentTab)`${TabStyles}`,

    Boundary: styled.div<BoundaryProps & HasChildren>`${BoundaryStyles}`,
    
    Grid: styled(EntityGrid)`${GridStyles}`,

  Header: styled(SliceEntityIconPageHeader)<HeaderProps>`${HeaderStyles}`,
};

export const ChallengeLayout: Shapeable.FC<ChallengeLayoutProps> = (props) => {
  const componentProps = useComponentProps(My.NAME, ChallengeLayoutDefaultProps, props) as ChallengeLayoutProps;
  const { className, children, entity } = componentProps;

  const { slices } = entity;

  const reflections = useReflections();
  const posts = usePosts();
  const videos = useVideos();
  const podcasts = usePodcasts();

  const allEntityTypes = useEntityTypes();

  const entityTypes = allEntityTypes.filter((type) => ['Reflection', 'Post', 'Video', 'Podcast'].includes(type.internalName))

  const [ activeEntityType, setActiveEntityType ] = React.useState<EntityTypeName | null>(null);

  // to save time, let's setup a local filter - we can move this to a URL based scheme later

  const byChallenge = (item: Entity & { challenges?: Challenge[] }) => {
    return (item.challenges || []).some(challenge => challenge.slug === entity?.slug);
  };
  
  const itemsByType = React.useMemo(() => {
    // filter items by the current challenge
    return {
      Reflection: reflections.filter(byChallenge),
      Post: posts.filter(byChallenge),
      Video: videos.filter(byChallenge),
      Podcast: podcasts.filter(byChallenge),
    };
  }, [entity?.slug]) as Partial<Record<EntityTypeName, Entity[]>>;

  const items: Entity[] = React.useMemo(() => {
    return [
      ...itemsByType.Reflection,
      ...itemsByType.Post,
      ...itemsByType.Video,
      ...itemsByType.Podcast,
    ];
  }, [itemsByType]);

  const activeItems = React.useMemo(() => {
    const list = activeEntityType === null
      ? items
      : (itemsByType[activeEntityType] || []);

    // newest first; videos without published fall back to created for sort only
    return sortBy(list, (item: Entity & { published?: string }) =>
      item.published || item.created
    ).reverse();
  }, [activeEntityType, items, itemsByType]);

  // console.log('ChallengeLayout', {
  //   entity,
  //   itemsByType,
  //   activeEntityType,
  //   activeItems,
  // });

  // Scott didnt want to show the name on the How To Live On Earth challenge page
  const pageEntity = usePageEntity();
  const isHowToLiveOnEarthChallenge = pageEntity?.slug === 'how-to-live-on-earth';
  
  return (
    <My.Container className={cls.name(className)}>
      <My.Header
        $isWordmarkTitle={isHowToLiveOnEarthChallenge}
        showName={!isHowToLiveOnEarthChallenge}
        iconProps={{ colors: { fill: isHowToLiveOnEarthChallenge ? '#F8CA0D' : 'strong' } }}
      />
      <SliceLayoutBannerTabs />

      <My.Boundary maxWidth={componentProps.maxBodyWidth}>
      <SlicesRenderer items={slices} />
      <My.Tabs>
        <My.Tab 
          onClick={() => setActiveEntityType(null)} 
          isActive={activeEntityType === null} 
          isLast={false} 
          label="All Types" 
          count={items.length} 
        />
        {
          entityTypes.map((type) => {
            const count = itemsByType?.[type.internalName as EntityTypeName]?.length || 0;

            if (count === 0) {
              return null; // don't show tabs for types with no items
            }

            return (
              <My.Tab 
                key={type.internalName} 
                wordWrap="nowrap"
                onClick={() => setActiveEntityType(type.internalName as EntityTypeName)} 
                isActive={activeEntityType === type.internalName} 
                isLast={false} 
                label={type.pluralLabel || type.name} 
                count={count} 
              />
            );
          }).filter(Boolean) // filter out nulls
        }
      </My.Tabs>

      <My.Grid 
        spacing={4}
        maxColumns={4}
        anyCardProps={{ showDescription: false }}
        items={activeItems} 
      />
      
      </My.Boundary>

    </My.Container>
  )
};

ChallengeLayout.cls = cls;